/**
 * Seed Data Fixture
 * 
 * Fallback data matching SEED_DATA.md for development when API is not ready.
 * Every function logs a console.warn in development — swap to API when ready.
 * 
 * Customers:
 *   - Duluth Aggregates: manual/both, 3 projects
 *   - Iron Range Concrete: beacon/supply_only
 *   - Mesabi Materials: hybrid/both
 */

const warn = (endpoint) => {
  if (import.meta.env.DEV) {
    console.warn(`[FIXTURE] Using seed data for ${endpoint} — swap to API`);
  }
};

// ============================================================================
// CUSTOMERS
// ============================================================================

const CUSTOMERS = [
  {
    id: 'cust-duluth',
    name: 'Duluth Aggregates',
    trackingMode: 'manual',
    supplyMode: 'both',
    status: 'Active',
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: 'cust-ironrange',
    name: 'Iron Range Concrete',
    trackingMode: 'beacon',
    supplyMode: 'supply_only',
    status: 'Active',
    createdAt: '2026-02-01T00:00:00Z',
    updatedAt: '2026-02-01T00:00:00Z',
  },
  {
    id: 'cust-mesabi',
    name: 'Mesabi Materials',
    trackingMode: 'hybrid',
    supplyMode: 'both',
    status: 'Active',
    createdAt: '2026-03-01T00:00:00Z',
    updatedAt: '2026-03-01T00:00:00Z',
  },
];

// ============================================================================
// PROJECTS
// ============================================================================

const PROJECTS = {
  'cust-duluth': [
    {
      id: 'proj-duluth-1',
      customerId: 'cust-duluth',
      name: 'Duluth Harbor Yard',
      trackingMode: 'manual',
      status: 'active',
      createdAt: '2026-01-20T00:00:00Z',
      updatedAt: '2026-01-20T00:00:00Z',
    },
    {
      id: 'proj-duluth-2',
      customerId: 'cust-duluth',
      name: 'Superior Transfer Station',
      trackingMode: 'manual',
      status: 'active',
      createdAt: '2026-02-10T00:00:00Z',
      updatedAt: '2026-02-10T00:00:00Z',
    },
    {
      id: 'proj-duluth-3',
      customerId: 'cust-duluth',
      name: 'Cloquet Spur',
      trackingMode: 'manual',
      status: 'paused',
      createdAt: '2026-03-05T00:00:00Z',
      updatedAt: '2026-03-05T00:00:00Z',
    },
  ],
  'cust-ironrange': [
    {
      id: 'proj-iron-1',
      customerId: 'cust-ironrange',
      name: 'Virginia Plant',
      trackingMode: 'beacon',
      status: 'active',
      createdAt: '2026-02-15T00:00:00Z',
      updatedAt: '2026-02-15T00:00:00Z',
    },
  ],
  'cust-mesabi': [
    {
      id: 'proj-mesabi-1',
      customerId: 'cust-mesabi',
      name: 'Hibbing Pit',
      trackingMode: 'hybrid',
      status: 'active',
      createdAt: '2026-03-10T00:00:00Z',
      updatedAt: '2026-03-10T00:00:00Z',
    },
    {
      id: 'proj-mesabi-2',
      customerId: 'cust-mesabi',
      name: 'Chisholm Stockpile',
      trackingMode: 'manual',
      status: 'active',
      createdAt: '2026-03-15T00:00:00Z',
      updatedAt: '2026-03-15T00:00:00Z',
    },
  ],
};

// ============================================================================
// SCOPES
// ============================================================================

const SCOPES = {
  'proj-duluth-1': [
    {
      id: 'scope-d1-yard',
      projectId: 'proj-duluth-1',
      name: 'Main Yard',
      trackingMode: 'manual',
      scopeType: 'yard',
      status: 'active',
      locationCount: 3,
    },
    {
      id: 'scope-d1-dock',
      projectId: 'proj-duluth-1',
      name: 'Dock Area',
      trackingMode: 'manual',
      scopeType: 'dock',
      status: 'active',
      locationCount: 2,
    },
  ],
  'proj-duluth-2': [
    {
      id: 'scope-d2-transfer',
      projectId: 'proj-duluth-2',
      name: 'Transfer Bay',
      trackingMode: 'manual',
      scopeType: 'transfer',
      status: 'active',
      locationCount: 2,
    },
  ],
  'proj-duluth-3': [
    {
      id: 'scope-d3-spur',
      projectId: 'proj-duluth-3',
      name: 'Rail Spur',
      trackingMode: 'manual',
      scopeType: 'rail',
      status: 'active',
      locationCount: 1,
    },
  ],
  'proj-mesabi-1': [
    {
      id: 'scope-m1-pit',
      projectId: 'proj-mesabi-1',
      name: 'Open Pit',
      trackingMode: 'hybrid',
      scopeType: 'pit',
      status: 'active',
      locationCount: 4,
    },
  ],
  'proj-mesabi-2': [
    {
      id: 'scope-m2-stock',
      projectId: 'proj-mesabi-2',
      name: 'Stockpile Area',
      trackingMode: 'manual',
      scopeType: 'stockpile',
      status: 'active',
      locationCount: 2,
    },
  ],
};

// ============================================================================
// LOCATIONS
// ============================================================================

const LOCATIONS = {
  'scope-d1-yard': [
    { id: 'loc-d1y-1', scopeId: 'scope-d1-yard', projectId: 'proj-duluth-1', name: 'Gate A', lat: 46.7867, lng: -92.1005, markerType: 'gate', status: 'active' },
    { id: 'loc-d1y-2', scopeId: 'scope-d1-yard', projectId: 'proj-duluth-1', name: 'Gate B', lat: 46.7872, lng: -92.0998, markerType: 'gate', status: 'active' },
    { id: 'loc-d1y-3', scopeId: 'scope-d1-yard', projectId: 'proj-duluth-1', name: 'Scale House', lat: 46.7870, lng: -92.1010, markerType: 'checkpoint', status: 'active' },
  ],
  'scope-d1-dock': [
    { id: 'loc-d1d-1', scopeId: 'scope-d1-dock', projectId: 'proj-duluth-1', name: 'Dock 1', lat: 46.7855, lng: -92.0985, markerType: 'dock', status: 'active' },
    { id: 'loc-d1d-2', scopeId: 'scope-d1-dock', projectId: 'proj-duluth-1', name: 'Dock 2', lat: 46.7858, lng: -92.0980, markerType: 'dock', status: 'active' },
  ],
  'scope-d2-transfer': [
    { id: 'loc-d2t-1', scopeId: 'scope-d2-transfer', projectId: 'proj-duluth-2', name: 'Bay North', lat: 46.7210, lng: -92.0850, markerType: 'bay', status: 'active' },
    { id: 'loc-d2t-2', scopeId: 'scope-d2-transfer', projectId: 'proj-duluth-2', name: 'Bay South', lat: 46.7205, lng: -92.0845, markerType: 'bay', status: 'active' },
  ],
  'scope-d3-spur': [
    { id: 'loc-d3s-1', scopeId: 'scope-d3-spur', projectId: 'proj-duluth-3', name: 'Spur End', lat: 46.6980, lng: -92.4520, markerType: 'rail', status: 'active' },
  ],
  'scope-m1-pit': [
    { id: 'loc-m1p-1', scopeId: 'scope-m1-pit', projectId: 'proj-mesabi-1', name: 'Pit North', lat: 47.4270, lng: -92.9380, markerType: 'pit', status: 'active' },
    { id: 'loc-m1p-2', scopeId: 'scope-m1-pit', projectId: 'proj-mesabi-1', name: 'Pit South', lat: 47.4250, lng: -92.9370, markerType: 'pit', status: 'active' },
    { id: 'loc-m1p-3', scopeId: 'scope-m1-pit', projectId: 'proj-mesabi-1', name: 'Crusher', lat: 47.4260, lng: -92.9400, markerType: 'equipment', status: 'active' },
    { id: 'loc-m1p-4', scopeId: 'scope-m1-pit', projectId: 'proj-mesabi-1', name: 'Haul Road', lat: 47.4265, lng: -92.9390, markerType: 'road', status: 'active' },
  ],
  'scope-m2-stock': [
    { id: 'loc-m2s-1', scopeId: 'scope-m2-stock', projectId: 'proj-mesabi-2', name: 'Pile A', lat: 47.4890, lng: -92.5230, markerType: 'stockpile', status: 'active' },
    { id: 'loc-m2s-2', scopeId: 'scope-m2-stock', projectId: 'proj-mesabi-2', name: 'Pile B', lat: 47.4895, lng: -92.5225, markerType: 'stockpile', status: 'active' },
  ],
};

// ============================================================================
// COUNTS (aggregated per location)
// ============================================================================

const COUNTS = {
  'proj-duluth-1': [
    { projectId: 'proj-duluth-1', sk: 'SCOPE#scope-d1-yard#LOC#loc-d1y-1', scopeId: 'scope-d1-yard', locationId: 'loc-d1y-1', supplyManual: 45, demandManual: 12, supplyBeacon: 0, demandBeacon: 0, lat: 46.7867, lng: -92.1005, lastEntryAt: '2026-09-01T14:30:00Z' },
    { projectId: 'proj-duluth-1', sk: 'SCOPE#scope-d1-yard#LOC#loc-d1y-2', scopeId: 'scope-d1-yard', locationId: 'loc-d1y-2', supplyManual: 22, demandManual: 8, supplyBeacon: 0, demandBeacon: 0, lat: 46.7872, lng: -92.0998, lastEntryAt: '2026-09-01T13:15:00Z' },
    { projectId: 'proj-duluth-1', sk: 'SCOPE#scope-d1-yard#LOC#loc-d1y-3', scopeId: 'scope-d1-yard', locationId: 'loc-d1y-3', supplyManual: 109, demandManual: 33, supplyBeacon: 0, demandBeacon: 0, lat: 46.7870, lng: -92.1010, lastEntryAt: '2026-09-01T15:00:00Z' },
    { projectId: 'proj-duluth-1', sk: 'SCOPE#scope-d1-dock#LOC#loc-d1d-1', scopeId: 'scope-d1-dock', locationId: 'loc-d1d-1', supplyManual: 67, demandManual: 20, supplyBeacon: 0, demandBeacon: 0, lat: 46.7855, lng: -92.0985, lastEntryAt: '2026-09-01T12:00:00Z' },
    { projectId: 'proj-duluth-1', sk: 'SCOPE#scope-d1-dock#LOC#loc-d1d-2', scopeId: 'scope-d1-dock', locationId: 'loc-d1d-2', supplyManual: 0, demandManual: 0, supplyBeacon: 0, demandBeacon: 0, lat: 46.7858, lng: -92.0980, lastEntryAt: null },
  ],
  'proj-mesabi-1': [
    { projectId: 'proj-mesabi-1', sk: 'SCOPE#scope-m1-pit#LOC#loc-m1p-1', scopeId: 'scope-m1-pit', locationId: 'loc-m1p-1', supplyManual: 30, demandManual: 15, supplyBeacon: 88, demandBeacon: 42, lat: 47.4270, lng: -92.9380, lastEntryAt: '2026-09-01T16:00:00Z' },
    { projectId: 'proj-mesabi-1', sk: 'SCOPE#scope-m1-pit#LOC#loc-m1p-2', scopeId: 'scope-m1-pit', locationId: 'loc-m1p-2', supplyManual: 0, demandManual: 0, supplyBeacon: 55, demandBeacon: 20, lat: 47.4250, lng: -92.9370, lastEntryAt: null },
  ],
};

// ============================================================================
// ENTRIES (individual count entries)
// ============================================================================

const ENTRIES = {
  'proj-duluth-1': [
    { id: 'entry-1', projectId: 'proj-duluth-1', scopeId: 'scope-d1-yard', locationId: 'loc-d1y-3', supplyDelta: 5, demandDelta: 0, channel: 'trucker-app', userId: 'user-trucker-1', userEmail: 'mike@duluth.com', timestamp: '2026-09-01T15:00:00Z', assetClass: 'default' },
    { id: 'entry-2', projectId: 'proj-duluth-1', scopeId: 'scope-d1-yard', locationId: 'loc-d1y-1', supplyDelta: -3, demandDelta: 2, channel: 'desktop', userId: 'user-admin-1', userEmail: 'admin@duluth.com', timestamp: '2026-09-01T14:30:00Z', assetClass: 'default' },
    { id: 'entry-3', projectId: 'proj-duluth-1', scopeId: 'scope-d1-dock', locationId: 'loc-d1d-1', supplyDelta: 10, demandDelta: 0, channel: 'trucker-app', userId: 'user-trucker-2', userEmail: 'joe@duluth.com', timestamp: '2026-09-01T12:00:00Z', assetClass: 'default' },
    { id: 'entry-4', projectId: 'proj-duluth-1', scopeId: 'scope-d1-yard', locationId: 'loc-d1y-2', supplyDelta: 0, demandDelta: -1, channel: 'desktop', userId: 'user-admin-1', userEmail: 'admin@duluth.com', timestamp: '2026-09-01T13:15:00Z', assetClass: 'default' },
  ],
  'proj-mesabi-1': [
    { id: 'entry-5', projectId: 'proj-mesabi-1', scopeId: 'scope-m1-pit', locationId: 'loc-m1p-1', supplyDelta: 8, demandDelta: 3, channel: 'trucker-app', userId: 'user-trucker-3', userEmail: 'carl@mesabi.com', timestamp: '2026-09-01T16:00:00Z', assetClass: 'default' },
  ],
};

// ============================================================================
// PUBLIC API (matches API response shapes)
// ============================================================================

export function getCustomers() {
  warn('GET /customers');
  return { items: CUSTOMERS, count: CUSTOMERS.length };
}

export function getProjects(customerId) {
  warn(`GET /projects?customerId=${customerId}`);
  const items = PROJECTS[customerId] || [];
  return { items, count: items.length };
}

export function getScopes(projectId) {
  warn(`GET /projects/${projectId}/scopes`);
  const scopes = SCOPES[projectId] || [];
  const items = scopes.map((scope) => ({
    ...scope,
    locations: LOCATIONS[scope.id] || [],
  }));
  return { items, count: items.length };
}

export function getLocations(projectId, scopeId) {
  warn(`GET /projects/${projectId}/scopes/${scopeId}/locations`);
  const items = LOCATIONS[scopeId] || [];
  return { items, count: items.length };
}

export function getCounts(projectId) {
  warn(`GET /projects/${projectId}/counts`);
  const items = COUNTS[projectId] || [];
  return { items, count: items.length };
}

export function getEntries(projectId, filters = {}) {
  warn(`GET /projects/${projectId}/entries`);
  let items = ENTRIES[projectId] || [];

  if (filters.scopeId) {
    items = items.filter((e) => e.scopeId === filters.scopeId);
  }
  if (filters.locationId) {
    items = items.filter((e) => e.locationId === filters.locationId);
  }
  if (filters.channel) {
    items = items.filter((e) => e.channel === filters.channel);
  }
  if (filters.userId) {
    items = items.filter((e) => e.userId === filters.userId);
  }

  return { items, count: items.length };
}
